
import Player from "@vimeo/player";
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { CaseStudy } from "@/slides/caseStudies";

@Component
export default class CaseStudyOverlay extends Vue {
  @Prop({ default: null }) study!: CaseStudy | null;
  @Prop({ default: false }) loading!: boolean;
  @Prop({ default: "" }) error!: string;
  @Prop({ default: "Case studies" }) categoryLabel!: string;
  @Prop({ default: false }) hasPrevious!: boolean;
  @Prop({ default: false }) hasNext!: boolean;
  videoIndex = 0;
  videoOpen = false;
  videoPlayerReady = false;
  videoPlayerFailed = false;
  videoPosterFailed = false;
  mediaImageFailed = false;
  logoFailed = false;
  failedGalleryImages: string[] = [];
  closing = false;
  private vimeoPlayer: Player | null = null;
  private videoLoadTimer: number | null = null;
  private entranceAnimations: Animation[] = [];
  private exitAnimation: Animation | null = null;
  private restorePage: (() => void) | null = null;
  private statisticFitFrame = 0;

  mounted() {
    // Mount outside the sticky slide's stacking context and overflow clipping.
    document.body.appendChild(this.$el);
    const root = document.documentElement;
    const app = document.getElementById("app");
    const previousFocus = document.activeElement as HTMLElement | null;
    const x = window.scrollX;
    const y = window.scrollY;
    const rootPaddingRight = root.style.paddingRight;
    const rootOverflow = root.style.overflow;
    const previousInert = app?.getAttribute("inert");
    const scrollbar = window.innerWidth - root.clientWidth;
    // Lock the document scrollport without repositioning body. Fixing body or
    // giving it overflow:hidden changes the sticky artwork's scroll container,
    // exposing the white slide underneath during the overlay fades.
    if (scrollbar) {
      root.style.paddingRight = `${
        parseFloat(getComputedStyle(root).paddingRight) + scrollbar
      }px`;
    }
    root.style.overflow = "hidden";
    app?.setAttribute("inert", "");
    this.restorePage = () => {
      root.style.paddingRight = rootPaddingRight;
      root.style.overflow = rootOverflow;
      if (previousInert === null || previousInert === undefined)
        app?.removeAttribute("inert");
      else app?.setAttribute("inert", previousInert);
      const behavior = root.style.scrollBehavior;
      root.style.scrollBehavior = "auto";
      window.scrollTo(x, y);
      root.style.scrollBehavior = behavior;
      previousFocus?.focus({ preventScroll: true });
    };
    if (!window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      (this.$el as HTMLElement).animate([{ opacity: 0 }, { opacity: 1 }], {
        duration: 180,
        easing: "ease-out",
      });
    }
    window.addEventListener("resize", this.scheduleStatisticFit);
    document.fonts?.ready.then(() => this.scheduleStatisticFit());
    this.resetStudy();
  }

  @Watch("study")
  resetStudy() {
    this.clearVideoLoadTimer();
    const previousPlayer = this.detachVimeoPlayer();
    this.videoOpen = false;
    this.videoIndex = 0;
    this.videoPlayerReady = false;
    this.videoPlayerFailed = false;
    this.videoPosterFailed = false;
    this.mediaImageFailed = false;
    this.logoFailed = false;
    this.failedGalleryImages = [];
    this.$nextTick(() => {
      previousPlayer?.destroy().catch(() => undefined);
      if (this.closing) return;
      this.entranceAnimations.forEach((animation) => animation.cancel());
      this.entranceAnimations = [];
      if (!window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        const elements = this.$el.querySelectorAll<HTMLElement>(
          ".case-study-topbar button, .case-study-topbar img, h1, .case-study-subtitle, .case-study-films"
        );
        this.entranceAnimations = Array.from(elements).map((element, index) =>
          element.animate(
            [
              { opacity: 0, transform: "translateY(8px)" },
              { opacity: 1, transform: "translateY(0)" },
            ],
            {
              duration: 240,
              delay: index * 30,
              easing: "ease-out",
              fill: "backwards",
            }
          )
        );
      }
      this.$el.scrollTop = 0;
      (this.$refs.back as HTMLButtonElement).focus({ preventScroll: true });
      this.scheduleStatisticFit();
    });
  }

  get videoPoster() {
    if (this.study?.videoPoster && !this.videoPosterFailed)
      return this.study.videoPoster;
    return require("@/assets/img/posters/our-story.jpg");
  }

  get visibleMediaImage() {
    if (!this.study || this.mediaImageFailed) return "";
    return this.study.mediaImage;
  }

  get hasLeadMedia() {
    return Boolean(this.study?.videos.length || this.visibleMediaImage);
  }

  get playingVideoSrc() {
    const source = this.study?.videos[this.videoIndex]?.src;
    if (!source) return "";
    const url = new URL(source);
    url.searchParams.set("autoplay", "1");
    url.searchParams.set("controls", "1");
    url.searchParams.set("fullscreen", "1");
    url.searchParams.set("loop", "0");
    return url.href;
  }

  get visibleGalleryImages() {
    if (!this.study) return [];
    return this.study.images.filter(
      (image) => !this.failedGalleryImages.includes(image.src)
    );
  }

  hideFailedLogo() {
    this.logoFailed = true;
  }

  hideFailedGalleryImage(src: string) {
    if (this.failedGalleryImages.includes(src)) return;
    this.failedGalleryImages = [...this.failedGalleryImages, src];
  }

  openVideo() {
    this.clearVideoLoadTimer();
    this.videoPlayerReady = false;
    this.videoPlayerFailed = false;
    this.videoOpen = true;
    this.videoLoadTimer = window.setTimeout(
      () => this.failVideoPlayer(),
      12000
    );
    this.$nextTick(() => {
      (this.$refs.videoClose as HTMLButtonElement | undefined)?.focus({
        preventScroll: true,
      });
    });
  }

  connectVimeoPlayer() {
    const frame = this.$refs.vimeoFrame as HTMLIFrameElement | undefined;
    if (!frame || !this.videoOpen || this.vimeoPlayer) return;
    try {
      const player = new Player(frame);
      this.vimeoPlayer = player;
      player.on("ended", this.closeVideo);
      player.on("error", this.failVideoPlayer);
      player
        .ready()
        .then(() => {
          if (this.vimeoPlayer !== player || !this.videoOpen) return;
          this.clearVideoLoadTimer();
          this.videoPlayerReady = true;
          this.videoPlayerFailed = false;
        })
        .catch(() => {
          if (this.vimeoPlayer === player) this.failVideoPlayer();
        });
    } catch (_) {
      this.failVideoPlayer();
    }
  }

  failVideoPlayer() {
    if (!this.videoOpen) return;
    this.clearVideoLoadTimer();
    this.videoPlayerReady = false;
    this.videoPlayerFailed = true;
  }

  clearVideoLoadTimer() {
    if (this.videoLoadTimer === null) return;
    window.clearTimeout(this.videoLoadTimer);
    this.videoLoadTimer = null;
  }

  closeVideo() {
    if (!this.videoOpen) return;
    this.clearVideoLoadTimer();
    const player = this.detachVimeoPlayer();
    this.videoOpen = false;
    this.videoPlayerReady = false;
    this.videoPlayerFailed = false;
    this.$nextTick(() => {
      player?.destroy().catch(() => undefined);
      (this.$refs.videoTrigger as HTMLButtonElement | undefined)?.focus({
        preventScroll: true,
      });
    });
  }

  detachVimeoPlayer() {
    if (!this.vimeoPlayer) return null;
    const player = this.vimeoPlayer;
    this.vimeoPlayer.off("ended", this.closeVideo);
    this.vimeoPlayer.off("error", this.failVideoPlayer);
    this.vimeoPlayer = null;
    return player;
  }

  scheduleStatisticFit() {
    cancelAnimationFrame(this.statisticFitFrame);
    this.statisticFitFrame = requestAnimationFrame(() => {
      this.statisticFitFrame = 0;
      this.fitStatisticDescription();
    });
  }

  fitStatisticDescription() {
    const statistic = this.$refs.statistic as HTMLElement | undefined;
    const value = this.$refs.statisticValue as HTMLElement | undefined;
    const description = this.$refs.statisticDescription as
      | HTMLElement
      | undefined;
    if (!statistic || !value || !description) return;

    const valueHeight = value.getBoundingClientRect().height;
    if (!valueHeight || !description.getBoundingClientRect().width) return;

    // The visible numeral is slightly shorter than its CSS line box. Fit the
    // description to that optical height and leave enough leading for letters
    // with descenders.
    const targetHeight = valueHeight * 0.85;
    const lineHeightRatio = 1.12;

    // Measure the complete description at each candidate size so no copy is
    // truncated when it needs more than three lines.
    description.style.display = "block";
    description.style.lineHeight = "1";

    const renderedLineCount = (fontSize: number) => {
      description.style.fontSize = `${fontSize}px`;
      const range = document.createRange();
      range.selectNodeContents(description);
      const lineTops = new Set<number>();
      Array.from(range.getClientRects()).forEach((rect) => {
        if (rect.width && rect.height) lineTops.add(Math.round(rect.top * 10));
      });
      range.detach();
      return Math.max(1, lineTops.size);
    };

    // Find the largest size whose complete text fits inside the number. Since
    // wrapping changes as the size changes, measure at every search step rather
    // than estimating the line count from character length.
    let lower = 1;
    let upper = targetHeight / lineHeightRatio;
    let fittedSize = lower;
    for (let index = 0; index < 12; index++) {
      const candidate = (lower + upper) / 2;
      const lineCount = renderedLineCount(candidate);
      if (candidate * lineHeightRatio * lineCount <= targetHeight + 0.5) {
        fittedSize = candidate;
        lower = candidate;
      } else {
        upper = candidate;
      }
    }

    const lineCount = renderedLineCount(fittedSize);
    description.style.fontSize = `${fittedSize.toFixed(2)}px`;
    description.style.lineHeight = `${(targetHeight / lineCount).toFixed(2)}px`;
    description.style.removeProperty("display");
  }

  close() {
    if (this.closing) return;
    this.closing = true;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      this.$emit("close");
      return;
    }
    // Keep the page locked and focus inside the dialog until the fade finishes.
    this.exitAnimation = (this.$el as HTMLElement).animate(
      [{ opacity: getComputedStyle(this.$el).opacity }, { opacity: 0 }],
      { duration: 140, easing: "ease-in", fill: "forwards" }
    );
    this.exitAnimation.onfinish = () => this.$emit("close");
  }

  onKeydown(event: KeyboardEvent) {
    if (event.key === "Escape") {
      event.preventDefault();
      if (this.videoOpen) {
        this.closeVideo();
        return;
      }
      this.close();
    }
    if (event.key !== "Tab") return;
    const scope = this.videoOpen
      ? (this.$refs.videoModal as HTMLElement)
      : (this.$el as HTMLElement);
    const controls = Array.from(
      scope.querySelectorAll<HTMLElement>(
        "button:not(:disabled), iframe, [tabindex='0']"
      )
    );
    const first = controls[0];
    const last = controls[controls.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  beforeDestroy() {
    this.clearVideoLoadTimer();
    this.detachVimeoPlayer()
      ?.destroy()
      .catch(() => undefined);
    window.removeEventListener("resize", this.scheduleStatisticFit);
    cancelAnimationFrame(this.statisticFitFrame);
    this.entranceAnimations.forEach((animation) => animation.cancel());
    if (this.exitAnimation) {
      this.exitAnimation.onfinish = null;
      this.exitAnimation.cancel();
    }
    this.restorePage?.();
    this.$el.parentNode?.removeChild(this.$el);
  }
}
